// ResocontoView.jsx — amm-v3 (A3, ondata 30): la vista "Resoconto d'insieme"
// (pagina 'resoconto') in React, flag-gated (shell_amm_v3_resoconto,
// default OFF).
//
// Replica 1:1 renderResoconto di admin-module.js r.2310-2434: stesso titolo
// e sottotitolo (con suffisso confronto), stessi bottoni (toggle 'Confronta
// con anno precedente', 'Esporta CSV', frecce anno), stessi 3 KPI, stessa
// MATRICE tipologia×mese con la semantica null-vs-0 (A3-080/A5-054: null =
// '·' senza drill, 0 = zero REALE mostrato), stessa riga TOTALE che preserva
// null, stesse colonne grigie anno-1 in confronto (NON cliccabili), stesso
// <details> 'N tipologie senza movimenti', stesso drill-down per cella
// (openResocontoDrill, modale read-only DELEGATO al v1).
//
// QUIRK v1 REPLICATI (parità bug-compatibile, pinnati nei test):
//  - il messaggio 'Nessun movimento nel {anno}' appare SOLO senza confronto
//    (r.2392: con confronto acceso la matrice resta visibile anche a zero);
//  - le opzioni della matrice/drill usano le tipologie dell'anno CORRENTE.
//
// DEVIAZIONI DICHIARATE:
//  D-A3.4 doppio click su 'Esporta CSV' = 1 richiesta (guardia ref; il v1
//         qui NON guarda — r.2423).
//  D-A3.2 ricarica fallita con dati presenti ⇒ banner + Riprova (famiglia
//         D-A1.5/D-A2.2); al primo giro errore pieno con Riprova.
//
// STATO (parità di persistenza): anno su AMM.resocontoAnno (default anno
// corrente, r.749), confronto su AMM.resocontoConfronto. COMMIT ATOMICO.

import React from 'react';
import { AM_NEW_TEXTS, MESI } from './api.js';
import {
  ARS_TEXTS, ARS_COLORS,
  resocontoStato, cellaStato, totaleMese, splitMovimenti, isTuttoZero,
  trovaPrec, subTesto, senzaMovimentiLabel, emptyMessage, readResoconto,
  esportaCsvResoconto, apriDrill,
} from './resoconto-api.js';

function StatoBox({ error, onRetry }) {
  if (error) {
    const msg = error && error.message ? error.message : String(error);
    return (
      <div role="alert" className="bg-white border rounded-lg p-6 text-center" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{'⚠ ' + msg}</div>
        <button
          type="button"
          style={{ marginTop: 12, background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
          onClick={onRetry}
        >{AM_NEW_TEXTS.riprova}</button>
      </div>
    );
  }
  return <div className="text-slate-400 text-sm">Caricamento…</div>;
}

// pallino colore tipologia (r.2322)
function Dot({ colore }) {
  return <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: colore || ARS_COLORS.dotFallback, marginRight: 6, flex: 'none' }} />;
}

export function ResocontoView({ win, tick }) {
  const A = win.AMM;
  const [pending, setPendingState] = React.useState(() => resocontoStato(A));
  const setPending = (next) => {
    A.resocontoAnno = next.anno; // write-through (r.2420-2422)
    A.resocontoConfronto = next.confronto;
    setPendingState(next);
  };
  const [dati, setDati] = React.useState({ loading: true, error: null, vista: null });
  const [reloadN, setReloadN] = React.useState(0);
  const busyRef = React.useRef(false); // guardia SINCRONA anti doppio-click (D-A3.4)

  React.useEffect(() => {
    let vivo = true;
    const anno = pending.anno;
    const confronto = pending.confronto;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readResoconto(win, anno, confronto).then(
      (r) => { if (vivo) setDati({ loading: false, error: null, vista: { anno, confronto, ...r } }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, pending.anno, pending.confronto]);

  const vista = dati.vista;
  const anno = vista ? vista.anno : pending.anno;
  const confronto = vista ? vista.confronto : pending.confronto;
  const t = vista ? vista.totali : { previsto: 0, pagato: 0, residuo: 0 };
  const tipologie = vista ? vista.tipologie : [];
  const tipologiePrec = vista ? vista.tipologiePrec : null;
  const { con: conMovimenti, senza: senzaMovimenti } = splitMovimenti(tipologie);
  const tuttoZero = vista ? isTuttoZero(t) : false;

  const onExportCsv = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      try { await esportaCsvResoconto(win, anno); }
      catch (e) { A.toast(ARS_TEXTS.exportFallito + (e && e.message ? e.message : e)); } // verbatim r.2425
    } finally {
      busyRef.current = false;
    }
  };

  // cella del mese corrente (r.2329-2336): null-vs-0, drill solo se non vuota
  const cellaCorrente = (tp, mm) => {
    const st = cellaStato(mm);
    if (st.vuota) return <td key={'c' + mm.mese} className="px-2 py-2 text-center text-slate-200 text-sm">{'·'}</td>;
    return (
      <td
        key={'c' + mm.mese}
        className="amm-res-cell px-2 py-2 text-right cursor-pointer hover:bg-cyan-50"
        data-tip={tp.id}
        data-mese={mm.mese}
        onClick={() => apriDrill(win, anno, tp, mm.mese)}
      >
        <div className={'text-xs tabular-nums ' + (st.haPrevisto ? 'text-slate-600' : 'text-slate-300')}>{st.haPrevisto ? A.fmtEur2(mm.previsto) : '—'}</div>
        {st.haPagato && <div className="text-xs text-emerald-700 tabular-nums">{A.fmtEur2(mm.pagato)}</div>}
      </td>
    );
  };

  // cella anno-1 (r.2344-2347): grigia, MAI cliccabile
  const cellaPrec = (mm, key) => (
    <td key={key} className="px-2 py-2 text-right bg-slate-50/60 border-l">
      {mm.previsto != null
        ? <div className="text-xs tabular-nums text-slate-400">{A.fmtEur2(mm.previsto)}</div>
        : <div className="text-xs text-slate-200">{'—'}</div>}
      {mm.pagato != null && <div className="text-xs tabular-nums text-emerald-600/70">{A.fmtEur2(mm.pagato)}</div>}
    </td>
  );

  const rigaTipologia = (tp) => {
    const tpPrec = confronto ? trovaPrec(tipologiePrec, tp) : null;
    const mesiPrec = tpPrec ? tpPrec.mesi : null;
    return (
      <tr key={tp.id} className="border-t">
        <td className="px-3 py-2 text-sm font-medium whitespace-nowrap">
          <div className="flex items-center"><Dot colore={tp.colore} />{tp.nome}</div>
        </td>
        {tp.mesi.map((mm, i) => {
          if (!confronto) return cellaCorrente(tp, mm);
          const mmPrec = mesiPrec ? (mesiPrec[i] || {}) : {};
          return [cellaCorrente(tp, mm), cellaPrec(mmPrec, 'p' + mm.mese)];
        })}
        <td className="px-3 py-2 text-right text-sm font-medium tabular-nums">{A.fmtEur2(tp.tot_previsto)}</td>
      </tr>
    );
  };

  const rigaTotale = () => (
    <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
      <td className="px-3 py-2 text-sm">{ARS_TEXTS.rigaTotale}</td>
      {MESI.map((_, i) => {
        const tm = totaleMese(tipologie, i + 1);
        const corr = (tm.previsto == null && tm.pagato == null)
          ? <td key={'t' + i} className="px-2 py-2 text-center text-slate-300 text-sm">{'·'}</td>
          : (
            <td key={'t' + i} className="px-2 py-2 text-right">
              <div className={'text-xs tabular-nums ' + (tm.previsto == null ? 'text-slate-300' : 'text-slate-700')}>{tm.previsto == null ? '—' : A.fmtEur2(tm.previsto)}</div>
              {tm.pagato != null && <div className="text-xs text-emerald-800 tabular-nums">{A.fmtEur2(tm.pagato)}</div>}
            </td>
          );
        if (!confronto) return corr;
        const tmPrec = totaleMese(tipologiePrec || [], i + 1);
        return [corr, cellaPrec(tmPrec, 'tp' + i)];
      })}
      <td className="px-3 py-2 text-right text-sm tabular-nums">{A.fmtEur2(t.previsto)}</td>
    </tr>
  );

  const thead = (
    <thead className="bg-slate-50 text-slate-500">
      <tr>
        <th className="px-3 py-2 text-xs uppercase font-medium text-left">{ARS_TEXTS.colTipologia}</th>
        {MESI.map((mLabel) => [
          <th key={mLabel} className="px-2 py-2 text-xs uppercase font-medium text-right">{mLabel}</th>,
          confronto ? <th key={mLabel + '-prec'} className="px-2 py-2 text-[10px] uppercase font-medium text-right text-slate-400 bg-slate-50/60 border-l">{anno - 1}</th> : null,
        ])}
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{ARS_TEXTS.colTotale}</th>
      </tr>
    </thead>
  );

  const tabella = (rows) => (
    <table className="w-full text-sm">
      {thead}
      <tbody>{rows}</tbody>
    </table>
  );

  return (
    <div data-amm-v3="resoconto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">{ARS_TEXTS.title}</h1>
          <div className="text-xs text-slate-400">{subTesto(confronto, anno)}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            id="res-confronto"
            type="button"
            className={(confronto ? 'bg-cyan-700 hover:bg-cyan-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-800') + ' text-xs px-3 py-2 rounded font-medium'}
            onClick={() => setPending({ ...pending, confronto: !pending.confronto })}
          >{confronto ? ARS_TEXTS.confrontoOn : ARS_TEXTS.confrontoOff}</button>
          <button id="amm-export-csv" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExportCsv}>{ARS_TEXTS.exportCsv}</button>
          <div className="flex items-center gap-1">
            <button id="res-prev" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => setPending({ ...pending, anno: pending.anno - 1 })}>{'‹'}</button>
            <span className="px-2 font-semibold tabular-nums">{anno}</span>
            <button id="res-next" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => setPending({ ...pending, anno: pending.anno + 1 })}>{'›'}</button>
          </div>
        </div>
      </div>

      {/* D-A3.2: ricarica fallita con dati presenti ⇒ banner + Riprova */}
      {dati.error && vista && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button
            type="button"
            style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
            onClick={() => setReloadN((n) => n + 1)}
          >{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      {!vista ? (
        <StatoBox error={dati.error} onRetry={() => setReloadN((n) => n + 1)} />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {[
              { label: ARS_TEXTS.kpiPrevisto, val: A.fmtEur2(t.previsto), color: ARS_COLORS.previsto },
              { label: ARS_TEXTS.kpiPagato, val: A.fmtEur2(t.pagato), color: ARS_COLORS.pagato },
              { label: ARS_TEXTS.kpiResiduo, val: A.fmtEur2(t.residuo), color: ARS_COLORS.residuo },
            ].map((k) => (
              <div key={k.label} className="bg-white border rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{k.label}</div>
                <div className="text-xl font-semibold mt-1" style={{ color: k.color }}>{k.val}</div>
              </div>
            ))}
          </div>

          {tuttoZero && !confronto ? (
            // QUIRK r.2392 replicato: il vuoto appare SOLO senza confronto
            <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">{emptyMessage(anno)}</div>
          ) : (
            <>
              <div className="bg-white border rounded-lg overflow-x-auto">
                {tabella([...conMovimenti.map(rigaTipologia), rigaTotale()])}
              </div>
              {senzaMovimenti.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs text-slate-400 cursor-pointer select-none">{senzaMovimentiLabel(senzaMovimenti.length)}</summary>
                  <div className="bg-white border rounded-lg overflow-x-auto mt-2">
                    {tabella(senzaMovimenti.map(rigaTipologia))}
                  </div>
                </details>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// Factory (pattern createAmmV3Cruscotto)
export function createAmmV3Resoconto(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0
      && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<ResocontoView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
